import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/cart";
import toast from "react-hot-toast";

const JustArrivedProducts = ({ limit = 6, categories = [] }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  const PALETTE = {
    PRIMARY_BLUE: "#6A9DD5",
    DARK_NAVY: "#1C2E4A",
    ACCENT_BROWN: "#7C4F2B",
  };

  const getJustArrivedProducts = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get("/api/v1/product/product-list/1");

      const all = data?.products || [];

      const sortedByDate = [...all].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      const seenCategories = new Set();
      const uniqueByCategory = [];

      for (const p of sortedByDate) {
        const catId = p?.category?._id || p?.category;
        if (!catId) continue;
        if (!seenCategories.has(catId)) {
          seenCategories.add(catId);
          uniqueByCategory.push(p);
        }
        if (uniqueByCategory.length >= limit) break;
      }

      setProducts(uniqueByCategory);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    getJustArrivedProducts();
  }, []);

  const handleAddToCart = (prod) => {
    const updated = [...cart, prod];
    setCart(updated);
    localStorage.setItem("cart", JSON.stringify(updated));
    toast.success("Item added to cart");
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-gray-500 text-lg">Loading...</div>
      </div>
    );
  }

  if (!products.length) return null;

  const mainImage = products[0];
  const secondImage = products[1] || products[0];
  const thirdImage = products[2] || products[1] || products[0];

  return (
    <section className="w-full py-12 md:py-16 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left: Text + categories + list */}
          <div className="space-y-6 md:space-y-7 max-w-md mx-auto lg:mx-0 w-full">
            <p className="text-[10px] md:text-xs tracking-[0.25em] uppercase text-gray-500 text-center lg:text-left">
              JUST ARRIVED.
            </p>

            <div className="text-center lg:text-left">
              <h2
                className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight uppercase"
                style={{ color: PALETTE.DARK_NAVY }}
              >
                Right Now.
                <br />
                Right Here.
              </h2>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                Discover the latest drops from Russh Berry — statement fits,
                elevated basics and limited pieces curated across our key
                categories.
              </p>
            </div>

            {/* Collection tags from parent categories */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {categories.slice(0, 6).map((cat) => (
                <span
                  key={cat._id || cat.slug || cat.name}
                  className="text-[10px] uppercase tracking-[0.2em] border border-gray-300 px-2 py-2 text-gray-700 text-center truncate hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/category/${cat.slug}`)}
                >
                  {cat.name}
                </span>
              ))}
            </div>

            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.2em] text-gray-400 text-center lg:text-left">
              Updated this week · Limited sizes only
            </p>

            {/* Highlight a few products as text list */}
            <div className="space-y-3">
              {products.slice(0, 3).map((p) => (
                <div
                  key={p._id}
                  className="flex items-center justify-between border-b border-gray-200 py-2 cursor-pointer hover:bg-gray-50 transition-colors px-2"
                  onClick={() => navigate(`/product/${p.slug}`)}
                >
                  <div className="flex flex-col max-w-[70%]">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-gray-500">
                      {p.category?.name || "Collection"}
                    </span>
                    <span className="text-sm text-gray-800 line-clamp-1 font-medium">
                      {p.name}
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 whitespace-nowrap ml-4">
                    {p.price?.toLocaleString("en-IN", {
                      style: "currency",
                      currency: "INR",
                    })}
                  </span>
                </div>
              ))}
            </div>

            {/* <p className="text-[10px] md:text-[11px] text-gray-500 text-center lg:text-left italic">
              Free shipping over ₹1,999 · easy returns on Just Arrived styles.
            </p> */}

            {/* Mobile Only: "Grab deal" text shown earlier */}
            <div className="block lg:hidden text-center mt-8">
              <h2
                className="text-3xl font-semibold leading-tight uppercase"
                style={{ color: PALETTE.DARK_NAVY }}
              >
                Grab your deal
                <br />
                Now...
              </h2>
            </div>
          </div>

          {/* Right: Image Grid */}
          <div className="w-full h-full mt-8 lg:mt-0">
            <div className="grid grid-cols-2 grid-rows-2 gap-3 md:gap-4 h-auto lg:h-[600px]">
              {/* Main Image (Large) - Spans 2 rows on Desktop, Full Width on Mobile? No, keeping grid layout */}
              <div className="col-span-2 lg:col-span-1 lg:row-span-2 h-64 sm:h-80 lg:h-auto">
                <div
                  className="w-full h-full cursor-pointer border border-gray-100 overflow-hidden group"
                  onClick={() =>
                    mainImage && navigate(`/product/${mainImage.slug}`)
                  }
                >
                  {mainImage && mainImage.photos && mainImage.photos[0] && (
                    <img
                      src={mainImage.photos[0]}
                      onError={(e) => {
                        e.target.src =
                          "/images/fallback-RB.png";
                      }}
                      alt={mainImage.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                </div>
              </div>

              {/* Second Image */}
              <div className="h-48 sm:h-64 lg:h-auto">
                <div
                  className="w-full h-full cursor-pointer border border-gray-100 overflow-hidden group"
                  onClick={() =>
                    secondImage && navigate(`/product/${secondImage.slug}`)
                  }
                >
                  {secondImage &&
                    secondImage.photos &&
                    secondImage.photos[0] && (
                      <img
                        src={secondImage.photos[0]}
                        onError={(e) => {
                          e.target.src =
                            "/images/fallback-RB.png";
                        }}
                        alt={secondImage.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    )}
                </div>
              </div>

              {/* Third Image */}
              <div className="h-48 sm:h-64 lg:h-auto">
                <div
                  className="w-full h-full cursor-pointer border border-gray-100 overflow-hidden group"
                  onClick={() =>
                    thirdImage && navigate(`/product/${thirdImage.slug}`)
                  }
                >
                  {thirdImage && thirdImage.photos && thirdImage.photos[0] && (
                    <img
                      src={thirdImage.photos[0]}
                      onError={(e) => {
                        e.target.src =
                          "/images/fallback-RB.png";
                      }}
                      alt={thirdImage.name}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Desktop Only: "Grab deal" text (bottom left) */}
          <div className="hidden lg:block col-span-1">
            <h2
              className="text-4xl md:text-5xl text-right font-semibold leading-tight uppercase mt-[-100px] relative z-10 bg-white/80 backdrop-blur-sm p-4 inline-block float-right"
              style={{ color: PALETTE.DARK_NAVY }}
            >
              Grab your deal
              <br />
              Now...
            </h2>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JustArrivedProducts;
